import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  ColorSwatch,
  Group,
  LoadingOverlay,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { randomId, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconPlus, IconTrash, IconWindow } from "@tabler/icons-react";
import { KeyboardEvent } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { sky_blue } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  TAG_API,
  TAG_COLORS,
  TAG_STATUS,
  TagObjectType,
  TblDmTagCategory,
} from "../../../model/TblDmTag";

type TagRow = {
  key: string;
  tagName: string;
  color: string;
  description: string;
};

type TagCreateFormValues = {
  categoryId: string | null;
  active: boolean;
  tags: TagRow[];
};

// Màu mặc định xoay vòng theo thứ tự dòng để các tag dễ phân biệt
const newRow = (index: number): TagRow => ({
  key: randomId(),
  tagName: "",
  color: TAG_COLORS[index % TAG_COLORS.length].value,
  description: "",
});

const isBlankRow = (row: TagRow) =>
  !row.tagName.trim() && !row.description.trim();

const ColorPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const label = TAG_COLORS.find((c) => c.value === value)?.label ?? value;

  return (
    <Popover opened={opened} onChange={toggle} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <ColorSwatch
          component="button"
          type="button"
          aria-label={`Màu hiển thị: ${label}`}
          color={`var(--mantine-color-${value}-6)`}
          size={28}
          withShadow={false}
          style={{ cursor: "pointer", border: "none", marginTop: 4 }}
          onClick={toggle}
        />
      </Popover.Target>
      <Popover.Dropdown p={8}>
        <Group gap={8} role="radiogroup" aria-label="Màu hiển thị">
          {TAG_COLORS.map((c) => {
            const selected = c.value === value;
            return (
              <ColorSwatch
                key={c.value}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={c.label}
                color={`var(--mantine-color-${c.value}-6)`}
                size={24}
                withShadow={false}
                style={{
                  cursor: "pointer",
                  border: "none",
                  boxShadow: selected
                    ? `0 0 0 2px #fff, 0 0 0 4px var(--mantine-color-${c.value}-6)`
                    : "none",
                }}
                onClick={() => {
                  onChange(c.value);
                  close();
                }}
              />
            );
          })}
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

const TagCreateForm = ({
  categories,
  objectTypes,
  defaultCategoryId,
  onSaved,
}: TagCreateFormProps) => {
  const [visible, { open, close }] = useDisclosure(false);

  const form = useForm<TagCreateFormValues>({
    initialValues: {
      categoryId:
        defaultCategoryId && categories.some((c) => c.id === defaultCategoryId)
          ? defaultCategoryId
          : null,
      active: true,
      tags: [newRow(0), newRow(1), newRow(2)],
    },
    validate: {
      categoryId: (value) => (value ? null : "Vui lòng chọn danh mục !"),
      tags: {
        tagName: (value, values, path) => {
          const index = Number(path.split(".")[1]);
          const row = values.tags[index];
          const filledRows = values.tags.filter((x) => !isBlankRow(x));
          // Dòng trống hoàn toàn sẽ được bỏ qua khi lưu, trừ khi không còn dòng nào
          if (row && isBlankRow(row) && filledRows.length > 0) return null;
          const name = value.trim();
          if (!name) return "Vui lòng nhập tên tag !";
          if (name.length > 255) return "Tên tag không được dài hơn 255 ký tự !";
          const duplicated =
            values.tags.filter(
              (x) => x.tagName.trim().toLowerCase() === name.toLowerCase()
            ).length > 1;
          if (duplicated) return "Tên tag bị trùng !";
          return null;
        },
      },
    },
  });

  const categoryOptions = objectTypes
    .map((o) => ({
      group: o.name,
      items: categories
        .filter((c) => c.type === o.code)
        .map((c) => ({ value: c.id, label: c.categoryName })),
    }))
    .filter((g) => g.items.length > 0);

  const selectedCategory = categories.find(
    (c) => c.id === form.values.categoryId
  );
  const selectedObjectName = objectTypes.find(
    (o) => o.code === selectedCategory?.type
  )?.name;

  const filledRows = form.values.tags.filter((x) => x.tagName.trim());

  const addRow = (focus: boolean = false) => {
    const row = newRow(form.values.tags.length);
    form.insertListItem("tags", row);
    if (focus) {
      setTimeout(() => document.getElementById(`tag-name-${row.key}`)?.focus(), 0);
    }
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    // Enter ở dòng cuối: thêm dòng mới để nhập nhanh
    if (e.key === "Enter") {
      e.preventDefault();
      if (index === form.values.tags.length - 1) addRow(true);
      else
        document
          .getElementById(`tag-name-${form.values.tags[index + 1].key}`)
          ?.focus();
    }
  };

  const handleSubmit = async (values: TagCreateFormValues) => {
    const rows = values.tags.filter((x) => !isBlankRow(x));
    open();
    const res = await repositoryPos.post<MessageResponse<string[]>>(
      TAG_API.createTags,
      {
        categoryId: values.categoryId,
        status: values.active ? TAG_STATUS.active : TAG_STATUS.inactive,
        tags: rows.map((x) => ({
          tagName: x.tagName.trim(),
          color: x.color,
          description: x.description.trim(),
        })),
      }
    );
    close();
    if (res?.success) {
      NotificationExtension.Success(`Thêm thành công ${rows.length} tag !`);
      onSaved();
      modals.closeAll();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={form.onSubmit(handleSubmit)}
      w={{ base: "320px", md: "680px" }}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Box mt={10}>
        <Select
          label="Danh mục"
          placeholder="Chọn danh mục tag"
          withAsterisk
          searchable
          data={categoryOptions}
          {...form.getInputProps("categoryId")}
        />
        {selectedObjectName ? (
          <Text size="xs" c="dark.4" mt={4}>
            Tag dùng cho đối tượng của danh mục:{" "}
            <Text span size="xs" fw={700} c="dark">
              {selectedObjectName}
            </Text>
          </Text>
        ) : null}
      </Box>

      <Group justify="space-between" mt="md" mb={6}>
        <Text size="sm" fw={500}>
          Danh sách tag{" "}
          <Text span size="sm" c="dark.4">
            ({filledRows.length})
          </Text>
        </Text>
        <Text size="xs" c="dark.4">
          Nhấn Enter để thêm dòng mới
        </Text>
      </Group>

      <Group gap={8} wrap="nowrap" mb={4} pr={44}>
        <Text size="xs" fw={600} c="dark.4" style={{ flex: 1 }}>
          Tên tag <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>
        </Text>
        <Text size="xs" fw={600} c="dark.4" w={28} ta="center">
          Màu
        </Text>
        <Text size="xs" fw={600} c="dark.4" style={{ flex: 1 }}>
          Mô tả
        </Text>
      </Group>

      <ScrollArea.Autosize mah={300} type="auto">
        <Stack gap={8}>
        {form.values.tags.map((row, index) => (
          <Group key={row.key} gap={8} wrap="nowrap" align="flex-start">
            <TextInput
              id={`tag-name-${row.key}`}
              placeholder={`Tag ${index + 1}`}
              aria-label={`Tên tag dòng ${index + 1}`}
              style={{ flex: 1 }}
              onKeyDown={(e) => handleNameKeyDown(e, index)}
              {...form.getInputProps(`tags.${index}.tagName`)}
            />
            <ColorPicker
              value={row.color}
              onChange={(value) => form.setFieldValue(`tags.${index}.color`, value)}
            />
            <TextInput
              placeholder="Mô tả (không bắt buộc)"
              aria-label={`Mô tả dòng ${index + 1}`}
              style={{ flex: 1 }}
              {...form.getInputProps(`tags.${index}.description`)}
            />
            <Tooltip label="Xóa dòng">
              <ActionIcon
                variant="subtle"
                color="red"
                size={36}
                aria-label={`Xóa dòng ${index + 1}`}
                disabled={form.values.tags.length === 1}
                onClick={() => form.removeListItem("tags", index)}
              >
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
        </Stack>
      </ScrollArea.Autosize>

      <Button
        type="button"
        variant="subtle"
        color="blue"
        leftSection={<IconPlus size={16} />}
        onClick={() => addRow(true)}
        px={6}
      >
        Thêm dòng
      </Button>

      <Group gap={6} mt="sm" mih={22}>
        <Text size="xs" c="dark.4">
          Xem trước
        </Text>
        {filledRows.length ? (
          filledRows.map((x) => (
            <Badge key={x.key} variant="light" radius="xl" color={x.color} tt="none">
              {x.tagName.trim()}
            </Badge>
          ))
        ) : (
          <Badge variant="light" radius="xl" color="gray" tt="none">
            [Tên tag]
          </Badge>
        )}
      </Group>

      <Checkbox
        mt="md"
        label="Sử dụng"
        {...form.getInputProps("active", { type: "checkbox" })}
      />

      <Group justify="end" mt="md">
        <Button
          type="button"
          color="gray"
          loading={visible}
          onClick={() => modals.closeAll()}
          leftSection={!visible ? <IconWindow size={18} /> : undefined}
        >
          Đóng
        </Button>
        <Button
          type="submit"
          color={sky_blue.base}
          loading={visible}
          leftSection={!visible ? <IconCheck size={18} /> : undefined}
        >
          Lưu{filledRows.length > 1 ? ` (${filledRows.length})` : ""}
        </Button>
      </Group>
    </Box>
  );
};

export default TagCreateForm;

type TagCreateFormProps = {
  categories: TblDmTagCategory[];
  objectTypes: TagObjectType[];
  defaultCategoryId?: string | null;
  onSaved: () => void;
};
